import { useCallback, useState } from "react";

import {
  useAdvancedCorporationAndPersonSearchLazyQuery,
  useAdvancedCorporationSearchLazyQuery,
  useSearchCustomerFolderByStringLazyQuery,
  useSearchPersonByFirstNameAndLastNameAndBirthdateLazyQuery,
  useSearchPersonByFirstNameAndLastNameLazyQuery,
} from "src/core/gql/graphql";
import { ICustomDuplicateFolder, ICustomDuplicatePerson } from "src/core/interfaces/Folders";
import {
  mapAdvancedCorporationSearchToDuplicateFolders,
  mapAdvancedPersonSearchToDuplicatePersons,
  mapFolderFilesToDuplicateFolders,
} from "src/core/mappers/folder.mapper";

export const useGetDuplicates = () => {
  const [duplicateFolders, setDuplicateFolders] = useState<
    ICustomDuplicateFolder[] | ICustomDuplicatePerson[]
  >([] as ICustomDuplicateFolder[] | ICustomDuplicatePerson[]);

  // Pour rechercher sur le SIREN pour les corporations
  const [advancedCorporationSearch] = useAdvancedCorporationSearchLazyQuery({
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
  });
  // Pour rechercher sur le SIREN pour les corporations et les pph
  const [advancedCorporationAndPersonSearch] = useAdvancedCorporationAndPersonSearchLazyQuery({
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
  });
  // Pour rechercher sur le nom et le prénom pour les pph
  const [searchPersonByFirstNameAndLastName] = useSearchPersonByFirstNameAndLastNameLazyQuery({
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
  });
  // Pour rechercher sur le nom, la date de naissance et le prénom pour les pph
  const [searchPersonByFirstNameAndLastNameAndBirthDate] =
    useSearchPersonByFirstNameAndLastNameAndBirthdateLazyQuery({
      fetchPolicy: "no-cache",
      nextFetchPolicy: "no-cache",
    });
  // Pour rechercher sur la raison sociale
  const [searchClients] = useSearchCustomerFolderByStringLazyQuery({
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
  });

  const resetDuplicates = useCallback(() => {
    setDuplicateFolders([]);
  }, []);

  /**
   * @description Retourne la liste des corporations dont le SIREN est le même que celui passé en paramètre.
   * @param siren - Le SIREN à rechercher.
   * @returns La liste des corporations dont le SIREN est le même que celui passé en paramètre.
   */
  const getDuplicatesBySiren = useCallback(
    async (siren: string): Promise<ICustomDuplicateFolder[]> => {
      const { data } = await advancedCorporationSearch({ variables: { values: [siren] } });

      const duplicates = mapAdvancedCorporationSearchToDuplicateFolders(data);
      setDuplicateFolders(duplicates);

      return duplicates;
    },
    [advancedCorporationSearch]
  );

  const getDuplicatesBySirenAllEntities = useCallback(
    async (siren: string): Promise<ICustomDuplicateFolder[]> => {
      const { data } = await advancedCorporationAndPersonSearch({ variables: { values: [siren] } });

      const duplicates = mapAdvancedCorporationSearchToDuplicateFolders(data);
      setDuplicateFolders(duplicates);

      return duplicates;
    },
    [advancedCorporationAndPersonSearch]
  );

  const getDuplicatesByName = useCallback(
    async (raisonSociale: string): Promise<ICustomDuplicateFolder[]> => {
      const { data } = await searchClients({
        variables: { searchString: raisonSociale, limit: 10 },
      });

      const duplicates = mapFolderFilesToDuplicateFolders(data);
      setDuplicateFolders(duplicates);

      return duplicates;
    },
    [searchClients]
  );

  const getDuplicatesByLastNameAndFirstNameAndBirthDate = useCallback(
    async (
      lastName: string,
      firstName: string,
      birthDate: string
    ): Promise<ICustomDuplicatePerson[]> => {
      const { data } = await searchPersonByFirstNameAndLastNameAndBirthDate({
        variables: { values: [firstName, lastName, birthDate] },
      });

      const duplicates = mapAdvancedPersonSearchToDuplicatePersons(data).filter(
        (duplicate) => duplicate.isCustomer === true
      );
      setDuplicateFolders(duplicates);

      return duplicates;
    },
    [searchPersonByFirstNameAndLastNameAndBirthDate]
  );

  const getDuplicatesByFirstNameAndLastName = useCallback(
    async (lastName: string, firstName: string): Promise<ICustomDuplicatePerson[]> => {
      const { data } = await searchPersonByFirstNameAndLastName({
        variables: { values: [firstName, lastName] },
      });

      const duplicates = mapAdvancedPersonSearchToDuplicatePersons(data).filter(
        (duplicate) => duplicate.isCustomer === true
      );
      setDuplicateFolders(duplicates);

      return duplicates;
    },
    [searchPersonByFirstNameAndLastName]
  );

  return {
    getDuplicatesBySiren,
    getDuplicatesByName,
    getDuplicatesBySirenAllEntities,
    getDuplicatesByLastNameAndFirstNameAndBirthDate,
    getDuplicatesByFirstNameAndLastName,
    resetDuplicates,
    duplicateFolders,
  };
};
